import type { ReactNode } from "react";
import { Skeleton } from "../dashboard/Skeleton";
import { Hint } from "./instance-detail/Hint";
import styles from "./ChartCard.module.css";

export type ChartCardState = "loading" | "empty" | "error" | "ready";

// The single panel shell for the instance dashboard. Owns the four async
// states so every panel is just "title + chart body"; adding a panel never
// re-implements loading/empty/error. `wide` makes it span the full grid row.
type ChartCardProps = {
  readonly title: string;
  readonly subtitle?: string | undefined;
  readonly hint?: string | undefined;
  readonly right?: ReactNode | undefined;
  readonly state: ChartCardState;
  readonly emptyText?: string | undefined;
  readonly errorText?: string | undefined;
  readonly wide?: boolean | undefined;
  readonly children: ReactNode;
};

export const ChartCard = ({
  title,
  subtitle,
  hint,
  right,
  state,
  emptyText,
  errorText,
  wide,
  children,
}: ChartCardProps): JSX.Element => (
  <section className={wide === true ? `${styles.card} ${styles.wide}` : styles.card}>
    <header className={styles.head}>
      <div className={styles.titles}>
        <h3 className={styles.title}>
          {title}
          {hint != null && <Hint text={hint} />}
        </h3>
        {subtitle != null && <p className={styles.subtitle}>{subtitle}</p>}
      </div>
      {right != null && <div className={styles.right}>{right}</div>}
    </header>
    <div className={state === "ready" || state === "loading" ? styles.body : `${styles.body} ${styles.bodyQuiet}`}>
      {state === "loading" && <Skeleton height="120px" />}
      {state === "empty" && <p className={styles.note}>{emptyText ?? "No data in this window."}</p>}
      {state === "error" && <p className={styles.note}>{errorText ?? "Failed to load."}</p>}
      {state === "ready" && children}
    </div>
  </section>
);
